import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import "./ui.css";

/**
 * ToastProvider – jediný systém hlášek pro celou aplikaci.
 *
 * Nahrazuje čtyři dosavadní mechanismy: lokální useToast (stav per komponenta
 * mizel při navigaci), utils/ToastMessage, window.alert a inline alerty pro
 * výsledek akce. Provider žije v kořeni App.tsx, takže toast přežije navigaci.
 *
 * Chování:
 *  - success/info/warning zmizí po 4 s, error zůstává do ručního zavření
 *    (sticky – chyba nesmí zmizet dřív, než si ji uživatel přečte);
 *  - zobrazují se nejvýš 3 najednou, starší nesticky se odsouvají;
 *  - kontejner má aria-live="polite", chybové toasty role="alert";
 *  - pozice: nahoře pod hlavičkou (--lks-toast-top nastavuje AppShell)
 *    (toast nesmí překrýt spodní navigaci).
 *
 * Starý hook useToast ze src/Toast.tsx deleguje sem, takže obrazovky
 * fungují bez úprav a migrují se postupně (kroky 3–13).
 */

export type ToastType = "success" | "error" | "warning" | "info";

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  icon: string;
  sticky: boolean;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, icon?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_ICONS: Record<ToastType, string> = {
  success: "bi-check-circle-fill",
  error: "bi-x-circle-fill",
  warning: "bi-exclamation-triangle-fill",
  info: "bi-info-circle-fill",
};

const AUTO_HIDE_MS = 4000;
const MAX_VISIBLE = 3;

let nextId = 1;

/**
 * Most pro starý src/Toast.tsx (deleguje na provider i mimo React strom).
 * Jakmile všechny obrazovky přejdou na useToast z tohoto souboru, zmizí.
 */
export let showToastGlobal: ToastContextValue["showToast"] = () => {
  // Provider ještě není namountovaný – hláška se zahodí (jen vývojová situace).
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Seznam žije v ref (jediný zdroj pravdy) a do stavu se jen kopíruje –
  // časovače a evikce tak nejsou vedlejší efekty uvnitř setState updaterů.
  const listRef = useRef<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeFromList = useCallback((id: number) => {
    listRef.current = listRef.current.filter((t) => t.id !== id);
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      removeFromList(id);
      setToasts([...listRef.current]);
    },
    [removeFromList]
  );

  const showToast = useCallback(
    (message: string, type: ToastType = "info", icon?: string) => {
      // Deduplikace: stejná hláška se nevrší – stará se odebere a nová
      // (s čerstvým časovačem) se zařadí na konec.
      const dup = listRef.current.find(
        (t) => t.message === message && t.type === type
      );
      if (dup) removeFromList(dup.id);

      const id = nextId++;
      const sticky = type === "error";
      listRef.current = [
        ...listRef.current,
        { id, message, type, icon: icon || DEFAULT_ICONS[type], sticky },
      ];

      // Evikce při přetečení: nejstarší nesticky kromě právě přidané;
      // když visí samé sticky chyby, padne nejstarší z nich (sticky chrání
      // jen před auto-hide, ne před vytlačením novějšími hláškami).
      while (listRef.current.length > MAX_VISIBLE) {
        const list = listRef.current;
        const candidate = list.find((t, i) => !t.sticky && i < list.length - 1);
        removeFromList((candidate ?? list[0]).id);
      }

      if (!sticky) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), AUTO_HIDE_MS)
        );
      }
      setToasts([...listRef.current]);
    },
    [dismiss, removeFromList]
  );

  const valueRef = useRef<ToastContextValue>({ showToast });
  valueRef.current.showToast = showToast;
  showToastGlobal = showToast;

  // Úklid časovačů při odmountování provideru (konec aplikace).
  React.useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((timer) => clearTimeout(timer));
      map.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={valueRef.current}>
      {children}
      <div className="lks-toasts" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`lks-toast lks-toast--${t.type}`}
            role={t.type === "error" ? "alert" : "status"}
          >
            <span className="lks-toast__icon" aria-hidden="true">
              <i className={`bi ${t.icon}`} />
            </span>
            <span className="lks-toast__text">{t.message}</span>
            <button
              type="button"
              className="lks-toast__close"
              aria-label="Zavřít hlášku"
              onClick={() => dismiss(t.id)}
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

/** Hlášky odkudkoli pod ToastProviderem: showToast("Uloženo.", "success"). */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Mimo provider (testy, izolovaný render) – bezpečný no-op.
    return { showToast: showToastGlobal };
  }
  return ctx;
}

export default ToastProvider;
